/**
 * Iter53 P37 — Extras din SaleForm.js (self-contained, read-only).
 *
 * Randează tab-ul "Audit & Tehnice" al formularului de vânzare:
 *   - Identificatori sistem (sf_id, ref, crm_internal_id, legacy)
 *   - Sursă import & reconciliere (import_source, batch_id, first_imported_at, ...)
 *   - Ownership (source_of_truth, override_flag)
 *   - Comisionare (commission_month, report_month, commissioned)
 *   - Contract — date sistem (agency, created_at)
 *
 * Toate câmpurile sunt `disabled readOnly` — modificările se fac exclusiv prin
 * pipeline-ul de import sau prin pagina dedicată de override. De aceea acest
 * component NU are handler-uri (handleChange / canWrite / isHidden) — depinde
 * doar de `formData`.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";

export const SaleFormAuditTab = ({ formData }) => (
  <>
    <div
      className="rounded-lg border border-slate-300 bg-slate-50 p-3 text-sm text-slate-700"
      data-testid="audit-tab-info-banner"
    >
      <strong>Notă:</strong> Toate câmpurile de pe acest tab sunt setate automat de sistem
      (import, sincronizare Salesforce, comisionare). Sunt afișate read-only pentru paritate
      completă View ↔ Edit. Modificările se fac doar prin pipeline-ul de import sau prin
      pagina dedicată de override.
    </div>

    {/* Identificatori sistem */}
    <Card data-testid="audit-section-identifiers">
      <CardHeader>
        <CardTitle className="text-base">Identificatori sistem</CardTitle>
        <CardDescription>ID-uri externe și interne ale vânzării.</CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>ID Salesforce</Label>
          <Input value={formData.sf_id} disabled readOnly data-testid="sf_id" />
        </div>
        <div className="space-y-2">
          <Label>Agent SF (ref ID)</Label>
          <Input value={formData.sf_agent_ref_id} disabled readOnly data-testid="sf_agent_ref_id" />
        </div>
        <div className="space-y-2">
          <Label>Agent comercial intern (ID)</Label>
          <Input value={formData.commercial_agent_id} disabled readOnly data-testid="commercial_agent_id" />
        </div>
        <div className="space-y-2">
          <Label>Agent în sursă (raw)</Label>
          <Input value={formData.raw_agent_full_name} disabled readOnly data-testid="raw_agent_full_name" />
        </div>
        <div className="space-y-2">
          <Label>ID CRM intern</Label>
          <Input value={formData.crm_internal_id} disabled readOnly data-testid="crm_internal_id" />
        </div>
        <div className="space-y-2">
          <Label>ID Legacy CRM</Label>
          <Input value={formData.external_legacy_id} disabled readOnly data-testid="external_legacy_id" />
        </div>
      </CardContent>
    </Card>

    {/* Import & reconciliere */}
    <Card data-testid="audit-section-import">
      <CardHeader>
        <CardTitle className="text-base">Sursă import &amp; reconciliere</CardTitle>
        <CardDescription>Provenienţa înregistrării și calitatea match-ului la importare.</CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Sursă import</Label>
          <Input value={formData.import_source} disabled readOnly data-testid="import_source" />
        </div>
        <div className="space-y-2">
          <Label>ID batch import</Label>
          <Input value={formData.import_batch_id} disabled readOnly data-testid="import_batch_id" />
        </div>
        <div className="space-y-2">
          <Label>Prima dată importat</Label>
          <Input value={formData.first_imported_at} disabled readOnly data-testid="first_imported_at" />
        </div>
        <div className="space-y-2">
          <Label>Status reconciliere</Label>
          <Input value={formData.reconciliation_status} disabled readOnly data-testid="reconciliation_status" />
        </div>
        <div className="space-y-2">
          <Label>Confidence match agent</Label>
          <Input value={formData.agent_match_confidence} disabled readOnly data-testid="agent_match_confidence" />
        </div>
        <div className="space-y-2">
          <Label>În Activare din</Label>
          <Input value={formData.activation_pending_since} disabled readOnly data-testid="activation_pending_since" />
        </div>
        <div className="space-y-2">
          <Label>Origine SF</Label>
          <Input value={formData.sales_origin} disabled readOnly data-testid="sales_origin" />
        </div>
        <div className="space-y-2">
          <Label>Sales Unit SF</Label>
          <Input value={formData.sales_unit_external_name} disabled readOnly data-testid="sales_unit_external_name" />
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label>Ultim sync Salesforce (contract)</Label>
          <Input value={formData.last_synced_from_sf_at} disabled readOnly data-testid="last_synced_from_sf_at" />
        </div>
      </CardContent>
    </Card>

    {/* Ownership (autoritate) */}
    <Card data-testid="audit-section-ownership">
      <CardHeader>
        <CardTitle className="text-base">Ownership (autoritate)</CardTitle>
        <CardDescription>Sursa și flagurile care guvernează RBAC + comisionare. Override-ul se face din pagina Detalii → Reasignare.</CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Sursă ownership</Label>
          <Input value={formData.ownership_source_of_truth} disabled readOnly data-testid="ownership_source_of_truth" />
        </div>
        <div className="space-y-2 flex items-center gap-2 pt-6">
          <Checkbox id="ownership_override_flag" checked={formData.ownership_override_flag} disabled data-testid="ownership_override_flag" />
          <Label htmlFor="ownership_override_flag" className="text-slate-500">Ownership override activ</Label>
        </div>
      </CardContent>
    </Card>

    {/* Comisionare */}
    <Card data-testid="audit-section-commission">
      <CardHeader>
        <CardTitle className="text-base">Comisionare</CardTitle>
        <CardDescription>Lunile de raport și starea de comisionare. Calculele se efectuează din pipeline-ul Comisioane.</CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Luna comisionare</Label>
          <Input value={formData.commission_month} disabled readOnly data-testid="commission_month" />
        </div>
        <div className="space-y-2">
          <Label>Luna raport</Label>
          <Input value={formData.report_month} disabled readOnly data-testid="report_month" />
        </div>
        <div className="space-y-2">
          <Label>Luna semnării</Label>
          <Input value={formData.signing_month} disabled readOnly data-testid="signing_month" />
        </div>
        <div className="space-y-2">
          <Label>Data comisionării</Label>
          <Input value={formData.commissioned_at} disabled readOnly data-testid="commissioned_at" />
        </div>
        <div className="space-y-2 flex items-center gap-2 pt-6">
          <Checkbox id="commissioned" checked={formData.commissioned} disabled data-testid="commissioned" />
          <Label htmlFor="commissioned" className="text-slate-500">Comisionat</Label>
        </div>
      </CardContent>
    </Card>

    {/* Contract — date sistem */}
    <Card data-testid="audit-section-contract-system">
      <CardHeader>
        <CardTitle className="text-base">Contract — date sistem</CardTitle>
        <CardDescription>Agenția deținătoare și momentul creării contractului.</CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Agenție (ID)</Label>
          <Input value={formData.agency} disabled readOnly data-testid="agency" />
        </div>
        <div className="space-y-2">
          <Label>Data introdus (created_at)</Label>
          <Input value={formData.created_at} disabled readOnly data-testid="created_at" />
        </div>
      </CardContent>
    </Card>
  </>
);

export default SaleFormAuditTab;
